import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import map from 'lodash/map';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { Button, Panel } from 'react-bootstrap';
import SmallLoader from '../../../../../components/SmallLoader';
import { formatEmc, formatSupplier } from '../../../../../shared/utils/run';
import { formatDateTime } from '../../../../../shared/utils/date';
import {
    manifestNumberFormatter,
    emaSmaManifestNumberFormatter,
} from '../../../../../shared/utils/rowFormatters';

const recoveryActionsFormatter = (cell, row) => {
    if (
        row.manifest.allManifestRecoveryActions &&
        row.manifest.allManifestRecoveryActions.length > 0 &&
        row.abnormality
    ) {
        return (
            <div>
                {row.manifest.allManifestRecoveryActions.map(rec => {
                    if (
                        row.abnormality.abnormalityType &&
                        row.abnormality.abnormalityId &&
                        rec.recoveryActionType &&
                        rec.recoveryActionId
                    ) {
                        return (
                            <p key={rec.recoveryActionId}>
                                <Link
                                    to={`runs/${row.abnormality.runId}/abnormality/${
                                        row.abnormality.abnormalityType
                                    }/${row.abnormality.abnormalityId}/recoveryAction/${
                                        rec.recoveryActionType
                                    }/${rec.recoveryActionId}`}
                                >
                                    {rec.name}
                                </Link>
                            </p>
                        );
                    }
                    return null;
                })}
            </div>
        );
    }

    return <div />;
};

export const abnormalitiesFormatter = (cell, row) => {
    if (row.otherAbnormalities && row.otherAbnormalities.length > 0) {
        return (
            <div>
                {map(row.otherAbnormalities, abn => {
                    if (abn.abnormalityId) {
                        return (
                            <p key={abn.abnormalityId}>
                                <Link
                                    to={`runs/${abn.runId}/abnormality/${abn.abnormalityType}/${
                                        abn.abnormalityId
                                    }`}
                                >
                                    {abn.name}
                                </Link>
                            </p>
                        );
                    }
                })}
            </div>
        );
    }

    return <div />;
};

const ManifestsInfo = ({
    manifests,
    handleSelect,
    handleSelectAll,
    linkUnlinkEmaSmaPending,
    toggleEmaPopup,
    unlinkEma,
    showEmaButtons,
    selectable,
}) => {
    const flattenedManifests = [];
    manifests.forEach(m =>
        flattenedManifests.push({
            ...m,
            manifestId: m.manifest.manifestId,
            manifestNumber: m.manifest.manifestNumber,
            emaSmaManifestId: m.manifest.emaSmaManifest ? m.manifest.emaSmaManifest.manifestId : '',
            emaSmaManifestNumber: m.manifest.emaSmaManifest
                ? m.manifest.emaSmaManifest.manifestNumber
                : '',
            supplier: formatSupplier({
                supplierCode: m.manifest.supplierCode,
                supplierPlantCode: m.manifest.supplierPlantCode,
            }),
            emc: formatEmc({
                emcCode: m.manifest.emcCode,
                emcPlantCode: m.manifest.emcPlantCode,
            }),
            emcDockCode: m.manifest.emcDockCode,
            connectingRunTime: m.manifest.connectingRunTime,
            pLaneExit: m.manifest.pLaneExit,
            orderGroup: m.manifest.orderGroup,
        })
    );

    return (
        <Panel>
            <BootstrapTable
                data={flattenedManifests}
                options={{ defaultSortName: 'manifestNumber', defaultSortOrder: 'asc' }}
                hover
                trClassName="clickable"
                selectRow={
                    selectable
                        ? {
                              mode: 'checkbox',
                              onSelect: handleSelect,
                              onSelectAll: handleSelectAll,
                          }
                        : {}
                }
            >
                <TableHeaderColumn dataField="manifestId" isKey hidden>
                    ID
                </TableHeaderColumn>
                <TableHeaderColumn
                    dataField="manifestNumber"
                    dataFormat={manifestNumberFormatter}
                    dataSort
                    filter={{ type: 'TextFilter', delay: 0 }}
                >
                    Manifest Number
                </TableHeaderColumn>
                <TableHeaderColumn
                    dataField="emaSmaManifestNumber"
                    dataFormat={emaSmaManifestNumberFormatter}
                >
                    EMA/SMA
                </TableHeaderColumn>
                <TableHeaderColumn
                    dataField="supplier"
                    dataSort
                    filter={{ type: 'TextFilter', delay: 0 }}
                >
                    Supplier
                </TableHeaderColumn>
                <TableHeaderColumn
                    dataField="emc"
                    dataSort
                    filter={{ type: 'TextFilter', delay: 0 }}
                >
                    EMC
                </TableHeaderColumn>
                <TableHeaderColumn
                    dataField="emcDockCode"
                    dataSort
                    filter={{ type: 'TextFilter', delay: 0 }}
                >
                    Dock
                </TableHeaderColumn>
                <TableHeaderColumn dataField="numberOfPallets"># of Pallets</TableHeaderColumn>
                <TableHeaderColumn dataField="connectingRunTime" dataFormat={formatDateTime}>
                    Connection time
                </TableHeaderColumn>
                <TableHeaderColumn dataField="pLaneExit" dataFormat={formatDateTime}>
                    P-Lane Exit
                </TableHeaderColumn>
                <TableHeaderColumn dataField="orderGroup">Order Group</TableHeaderColumn>
                <TableHeaderColumn
                    dataField="existingAbnormalities"
                    dataFormat={(cell, row) => abnormalitiesFormatter(cell, row)}
                >
                    Other Abnormalities
                </TableHeaderColumn>
                <TableHeaderColumn
                    dataField="manifest"
                    dataFormat={(cell, row) => recoveryActionsFormatter(cell, row)}
                >
                    Other Recovery Actions
                </TableHeaderColumn>
                <TableHeaderColumn
                    dataField="manifest"
                    hidden={!showEmaButtons}
                    dataFormat={(cell, row) => (
                        <div>
                            {!row.manifest.emaSmaManifest && (
                                <Button
                                    bsStyle="info"
                                    bsSize="xsmall"
                                    block
                                    onClick={() => toggleEmaPopup(row.manifestId)}
                                >
                                    {linkUnlinkEmaSmaPending ? (
                                        <SmallLoader noMargin tiny />
                                    ) : (
                                        'Link'
                                    )}
                                </Button>
                            )}
                            {row.manifest.emaSmaManifest && (
                                <Button
                                    bsStyle="danger"
                                    bsSize="xsmall"
                                    block
                                    onClick={() => unlinkEma(row.manifestId)}
                                >
                                    {linkUnlinkEmaSmaPending ? (
                                        <SmallLoader noMargin tiny />
                                    ) : (
                                        'Unlink'
                                    )}
                                </Button>
                            )}
                        </div>
                    )}
                >
                    Link EMA/SMA
                </TableHeaderColumn>
            </BootstrapTable>
        </Panel>
    );
};

ManifestsInfo.propTypes = {
    manifests: PropTypes.array,
    handleSelect: PropTypes.func,
    selectable: PropTypes.bool,
};
ManifestsInfo.defaultProps = {
    manifests: [],
    selectable: true,
};

export default ManifestsInfo;
